import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { env } from '../config/env';
import { oauth2Config } from '../config/oauth2.config';
import { SocialLoginButtons } from '../components/SocialLoginButtons';

export const RegisterPage: React.FC = () => {
  const { register, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const from = (location.state as any)?.from?.pathname || '/';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    try {
      await register(name, email, password);
      navigate(from, { replace: true });
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.response?.data?.message || 'Registration failed. Please try again.';
      setError(message);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-[480px] space-y-10">
        <div className="text-center space-y-4">
          <Link to="/" className="flex flex-col items-center gap-4">
            {env.LOGO_URL ? (
              <img src={env.LOGO_URL} alt={env.COMPANY_NAME} className="h-12 w-auto object-contain" />
            ) : (
              <div className="size-12 bg-primary rounded-xl flex items-center justify-center text-white">
                <span className="material-symbols-outlined text-3xl">shopping_bag</span>
              </div>
            )}
            <h1 className="text-3xl font-black tracking-tight uppercase text-[#111418] dark:text-white">
              {env.COMPANY_NAME}
            </h1>
          </Link>
          <p className="text-sm font-medium text-gray-500 max-w-xs mx-auto">Create your account</p>
        </div>

        <div className="bg-white dark:bg-black p-10 md:p-12 rounded-[2.5rem] border border-border/40 shadow-2xl shadow-primary/5 space-y-8">
          <form onSubmit={handleRegister} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-semibold">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Enter your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="h-12 rounded-xl border border-gray-200 dark:border-gray-700 focus-visible:ring-2 focus-visible:ring-primary/50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-semibold">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 rounded-xl border border-gray-200 dark:border-gray-700 focus-visible:ring-2 focus-visible:ring-primary/50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-semibold">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="h-12 rounded-xl border border-gray-200 dark:border-gray-700 pr-12 focus-visible:ring-2 focus-visible:ring-primary/50"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-500" />
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-semibold">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-12 rounded-xl border border-gray-200 dark:border-gray-700 focus-visible:ring-2 focus-visible:ring-primary/50"
                />
              </div>

              {error && (
                <div className="text-red-500 text-sm font-medium">{error}</div>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                size="lg"
                className="w-full h-14 rounded-2xl text-base font-black shadow-xl shadow-primary/10 transition-all hover:-translate-y-0.5 bg-[#111418] dark:bg-white text-white dark:text-black flex items-center justify-center gap-3 border-0"
              >
                {isLoading ? 'Creating account...' : 'Create account'}
              </Button>
            </div>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-100 dark:border-gray-800"></span>
            </div>
            <div className="relative flex justify-center text-[10px] font-black uppercase tracking-widest leading-none">
              <span className="bg-white dark:bg-black px-4 text-gray-300">Or sign up with</span>
            </div>
          </div>

          <SocialLoginButtons variant="signup" returnTo={from} />
        </div>

        <div className="flex items-center justify-center gap-6">
          <Link to={`/login?client_id=${oauth2Config.clientId}`} className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 hover:text-primary transition-colors flex items-center gap-2">
            Already have an account? Sign in <ArrowRight className="size-3" />
          </Link>
        </div>
      </div>
    </div>
  );
};
